/**
 * Single catalog of authenticated sidebar destinations.
 * Factory nav is project-scoped; Team nav is team-scoped.
 * Plan and Review open from board cards, so they are not rail entries.
 */

import {
  Activity,
  BookOpen,
  Bot,
  FolderKanban,
  GitBranch,
  Home,
  LayoutPanelLeft,
  Link2,
  Rocket,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { routes } from '../utils/navigation';
import { isBoardView, type BoardView } from '../shared/views';

export interface NavItem {
  id: string;
  path: string;
  labelKey: string;
  icon: LucideIcon;
  /** When set, match child routes under this path as active. */
  matchPrefix?: string;
}

export interface NavSection {
  id: string;
  labelKey: string;
  items: NavItem[];
}

/** Factory (project-scoped) rail. */
export const FACTORY_NAV_ITEMS: NavItem[] = [
  { id: 'home', path: routes.home(), labelKey: 'nav.home', icon: Home },
  { id: 'roadmap', path: routes.roadmap(), labelKey: 'nav.roadmap', icon: GitBranch },
  {
    id: 'work',
    path: routes.work(),
    labelKey: 'nav.work',
    icon: FolderKanban,
    matchPrefix: '/work',
  },
  {
    id: 'deployments',
    path: routes.deployments(),
    labelKey: 'nav.deployments',
    icon: Rocket,
    matchPrefix: '/deployments',
  },
  { id: 'stories', path: routes.stories(), labelKey: 'nav.stories', icon: BookOpen },
  {
    id: 'agents',
    path: routes.agents(),
    labelKey: 'nav.agents',
    icon: Bot,
    matchPrefix: '/agents',
  },
  {
    id: 'console',
    path: routes.console(),
    labelKey: 'nav.console',
    icon: LayoutPanelLeft,
    matchPrefix: '/console',
  },
  {
    id: 'automations',
    path: routes.automations(),
    labelKey: 'nav.automations',
    icon: Workflow,
    matchPrefix: '/automations',
  },
  {
    id: 'connections',
    path: routes.connections(),
    labelKey: 'nav.connections',
    icon: Link2,
    matchPrefix: '/connections',
  },
  {
    id: 'status',
    path: routes.status(),
    labelKey: 'nav.status',
    icon: Activity,
    matchPrefix: '/status',
  },
];

/** Team admin rail. */
export const TEAM_NAV_ITEMS: NavItem[] = [
  {
    id: 'team',
    path: routes.team(),
    labelKey: 'nav.team',
    icon: Users,
    matchPrefix: '/team',
  },
];

/** Factory + Team sections for the left rail. */
export const NAV_SECTIONS: NavSection[] = [
  { id: 'factory', labelKey: 'nav.factory', items: FACTORY_NAV_ITEMS },
  { id: 'team', labelKey: 'nav.teamSection', items: TEAM_NAV_ITEMS },
];

/** Exact match for home; prefix match for nested section routes. */
export function isNavItemActive(pathname: string, itemPath: string, matchPrefix?: string): boolean {
  const pathOnly = itemPath.split('?')[0] || itemPath;
  if (pathOnly === '/') {
    return pathname === '/';
  }
  if (matchPrefix) {
    return pathname === matchPrefix || pathname.startsWith(`${matchPrefix}/`);
  }
  return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`);
}

/**
 * Map a pathname (+ optional search) to a board view id.
 * Home `/` is not a board; legacy paths still resolve for tests and deep links.
 */
export function viewFromPath(pathname: string, search = ''): BoardView | null {
  if (pathname === '/work/support' || pathname === '/support') return 'support';
  if (pathname === '/work/bugs' || pathname === '/bugs') return 'bugs';

  if (pathname === '/work/coding') {
    const view = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(
      'view',
    );
    if (view === 'failed-pipelines') return null;
    if (view && isBoardView(view)) return view;
    return 'wip';
  }

  // Legacy deep links (redirect targets still hit these before Navigate).
  if (pathname === '/wip') return 'wip';
  if (pathname === '/backlog') return 'backlog';
  if (pathname === '/all') return 'all';
  if (pathname === '/done') return 'done';
  if (pathname === '/open-prs') return 'openPrs';
  if (pathname === '/pipelines') return 'pipelines';
  return null;
}
